import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import {
  EligibilityCriterion,
  Form,
  FormDefault,
  FormDefaultType,
  FormInfo,
  FormRequest,
  FormResponse,
  FormResponseStatus,
  LanguageCode,
  Page,
} from "./dtos";

const FORM_SERVICE = `${Constants.API_URL}/bedrock/forms`;
const FORM_RESPONSE_SERVICE = `${Constants.API_URL}/bedrock/form_responses`;

export type ExtraFormParams = "eligibility_criterions";
export interface FormFilterParams {
  search?: string;
  active?: boolean;
  page?: number;
  perPage?: number;
  extraFields?: ExtraFormParams[];
}

type ExtraFormRequestParams = "form" | "form_responses";

export interface FormRequestsFilterParams {
  playerId?: number;
  fanId?: number;
  formId?: number;
  extraFields?: ExtraFormRequestParams[];
  page?: number;
  perPage?: number;
}

export interface FormResponsesFilterParams {
  playerId?: number;
  fanId?: number;
  formId?: number;
  formRequestId?: number;
  status?: FormResponseStatus[];
  page?: number;
  perPage?: number;
}

export interface UpsertFormRequest {
  displayName?: string;
  active?: boolean;
  contentPageThemeId?: number;
  allowThemeChange?: boolean;
  allowAiPersonalization?: boolean;
  allowTranslations?: boolean;
  supportedLanguages: LanguageCode[];
  introTitle?: string;
  introText?: string;
  outroTitle?: string;
  outroText?: string;
  outroCtaLabel?: string;
  outroCtaType?: "player_link" | "player_email" | "player_phone";
  outroCtaLink?: string;
  formFields?: Array<{
    id?: number;
    title: string;
    subtitle?: string;
    fieldType: string;
    required?: boolean;
    position?: number;
    fieldOptions?: Record<string, any>;
    active?: boolean;
  }>;
  eligibilityCriterions?: Array<EligibilityCriterion>;
}

export interface FormRequestParams {
  playerId: number;
  fanId?: number;
  isTest?: boolean;
  useAiPersonalization?: boolean;
  contentPageThemeId?: number;
  internalIdentifier?: string;
  contextInstructions?: string;
}

export interface DefaultFormParams {
  playerId?: number;
  fanId?: number;
  formType: FormDefaultType;
}

interface FormDefaultRequest {
  formType: FormDefaultType;
  formId: number;
  segmentationDetailId?: number;
}

export interface UpdateFormDefaultsRequest {
  defaults: FormDefaultRequest[];
}

export class FormServices {
  @scCatchServiceException()
  static async getForms(params?: FormFilterParams): Promise<Page<Form>> {
    const { data } = await axios.get<Page<Form>>(FORM_SERVICE, { params });
    return data;
  }

  @scCatchServiceException()
  static async getForm(id: string | number, params?: { extraFields?: ExtraFormParams[] }): Promise<Form> {
    const { data } = await axios.get<Form>(`${FORM_SERVICE}/${id}`, { params });
    return data;
  }

  @scCatchServiceException()
  static async createForm(request: UpsertFormRequest): Promise<Form> {
    const { data } = await axios.post<Form>(FORM_SERVICE, request);
    return data;
  }

  @scCatchServiceException()
  static async updateForm(id: string | number, request: UpsertFormRequest): Promise<Form> {
    const { data } = await axios.patch<Form>(`${FORM_SERVICE}/${id}`, request);
    return data;
  }

  @scCatchServiceException()
  static async deleteForm(id: string | number): Promise<void> {
    await axios.delete(`${FORM_SERVICE}/${id}`);
  }

  @scCatchServiceException()
  static async generateForm(userInstructions: string): Promise<FormInfo> {
    const { data } = await axios.post<FormInfo>(`${FORM_SERVICE}/generate`, { userInstructions });
    return data;
  }

  @scCatchServiceException()
  static async refineFormInfo(userInstructions: string, form: FormInfo): Promise<FormInfo | Form> {
    const { data } = await axios.post<Form>(`${FORM_SERVICE}/refine`, { userInstructions, form });
    return data;
  }

  @scCatchServiceException()
  static async refineForm(userInstructions: string, formId: number): Promise<FormInfo | Form> {
    const { data } = await axios.post<Form>(`${FORM_SERVICE}/${formId}/refine`, { userInstructions });
    return data;
  }

  @scCatchServiceException()
  static async getFormRequests(params?: FormRequestsFilterParams): Promise<Page<FormRequest>> {
    const { data } = await axios.get<Page<FormRequest>>(`${FORM_SERVICE}/requests`, { params });
    return data;
  }

  @scCatchServiceException()
  static async getFormResponses(params?: FormResponsesFilterParams): Promise<Page<FormResponse>> {
    const { data } = await axios.get<Page<FormResponse>>(`${FORM_RESPONSE_SERVICE}`, { params });
    return data;
  }

  @scCatchServiceException()
  static async getDefaultFormRequest(params: DefaultFormParams): Promise<FormRequest> {
    const { data } = await axios.get<FormRequest>(`${FORM_SERVICE}/default_request`, { params });
    return data;
  }

  @scCatchServiceException()
  static async createFormRequest(formId: string | number, params: FormRequestParams): Promise<FormRequest> {
    const { data } = await axios.post<FormRequest>(`${FORM_SERVICE}/${formId}/requests`, params);
    return data;
  }

  @scCatchServiceException()
  static async updateFormRequest(
    formId: string | number,
    formRequestId: number,
    params: FormRequestParams
  ): Promise<FormRequest> {
    const { data } = await axios.patch<FormRequest>(`${FORM_SERVICE}/${formId}/requests/${formRequestId}`, params);
    return data;
  }

  @scCatchServiceException()
  static async getCoachDefaults(): Promise<FormDefault[]> {
    const { data } = await axios.get<FormDefault[]>(`${FORM_SERVICE}/get_coach_defaults`);
    return data;
  }

  @scCatchServiceException()
  static async updateCoachDefaults(request: UpdateFormDefaultsRequest): Promise<void> {
    await axios.post<FormDefault[]>(`${FORM_SERVICE}/update_coach_defaults`, request);
  }
}
